import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import AnnouncementBar from "@/components/AnnouncementBar";

const LINKS = [
  { to: "/products", label: "Products" },
  { to: "/accessories", label: "Accessories" },
  { to: "/videos", label: "Videos" },
  { to: "/reviews", label: "Reviews" },
  { to: "/about", label: "About" },
  { to: "/distributors", label: "Distributors" },
  { to: "/contact", label: "Contact" },
];

const DesktopLinks = ({ pathname }) => (
  <nav className="hidden md:flex items-center gap-5 lg:gap-8">
    {LINKS.map((l) => {
      const idle = pathname === l.to ? "var(--sun-amber)" : "var(--ink-soft)";
      return (
        <Link
          key={l.to}
          to={l.to}
          data-testid={`nav-${l.label.toLowerCase()}`}
          className="text-sm font-medium transition-colors"
          style={{ color: idle }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "var(--ink)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = idle; }}
        >
          {l.label}
        </Link>
      );
    })}
  </nav>
);

const CartButton = ({ count }) => (
  <Link
    data-testid="nav-cart"
    to="/cart"
    aria-label="View cart"
    className="relative inline-flex items-center justify-center w-11 h-11 rounded-full transition-colors"
    style={{ border: "1px solid var(--border)", color: "var(--ink)" }}
  >
    <ShoppingBag size={18} />
    {count > 0 && (
      <span
        data-testid="cart-count"
        className="absolute -top-1 -right-1 text-xs font-medium px-1.5 py-0.5 rounded-full"
        style={{ background: "var(--sun-orange)", color: "#FCF6EA", minWidth: 18, textAlign: "center" }}
      >
        {count}
      </span>
    )}
  </Link>
);

const MobileMenu = ({ onNavigate }) => (
  <div className="md:hidden border-t" style={{ borderColor: "var(--border)" }}>
    <div className="max-w-[1400px] mx-auto px-6 py-4 flex flex-col gap-3">
      {LINKS.map((l) => (
        <Link
          key={l.to}
          to={l.to}
          data-testid={`mnav-${l.label.toLowerCase()}`}
          onClick={onNavigate}
          className="py-2 text-base font-medium"
          style={{ color: "var(--ink)" }}
        >
          {l.label}
        </Link>
      ))}
    </div>
  </div>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { count } = useCart() || { count: 0 };
  const { pathname } = useLocation();

  return (
    <header
      data-testid="main-navbar"
      className="theme-deep nav-deep sticky top-0 z-40 backdrop-blur-md"
    >
      <AnnouncementBar />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="brand-mark" style={{ fontSize: "1.4rem" }}>
          <span className="dot" />
          <span className="word">SunPress</span>
          <span className="sub">Solar</span>
        </Link>
        <DesktopLinks pathname={pathname} />
        <div className="flex items-center gap-3">
          <CartButton count={count} />
          <button
            data-testid="nav-mobile-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full"
            style={{ border: "1px solid var(--border)", color: "var(--ink)" }}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      {open && <MobileMenu onNavigate={() => setOpen(false)} />}
    </header>
  );
}
